import React, { useEffect, useId, useRef, useState } from 'react';
import { supabaseClient } from '../../config/supabase';
import WebPushDeviceControl from '../shared/WebPushDeviceControl';
import { NOTIFICATION_HELP_CONTENT } from '../shared/notificationHelpContent';
import Switch from '../ui/Switch';
import Checkbox from '../ui/Checkbox';
import HelpButton from '../ui/HelpButton';
import AlertSoundsCard from './AlertSoundsCard';
import LiveAlertsStatus from '../notifications/LiveAlertsStatus';
import PhoneInput from '../ui/PhoneInput';
import AppLink from '../ui/AppLink';
import { useSaveToast } from '../ui/SaveToast';
import { toE164 } from '../../lib/phone';
import { loadCurrentCompanyName } from '../../lib/currentCompany';
import {
    NOTIFICATION_PREFERENCE_CHANNELS,
    notificationPreferenceChannels,
    buildOverrideMap,
    normalizeNotificationWorkflows,
} from '../../lib/notificationPreferences';

// BL-177 Step 2d (section 5): Settings > Communications > Notifications (it was "My notifications" until Step 2j (E)).
//   Conversation privacy and the Enter-key switch moved out to their own tiles in Settings > Communications.
//   Top cards: This device (switch, "Registered to you" read back from the DB, Send a test) + My mobile number.
//   Personal channel checkboxes for the staff alerts that apply to this person. A person can turn OFF any channel the
//   company has on, never turn ON one the company has off (those are grey, disabled, and the save path refuses).
//   Personal quiet hours (user_quiet_hours) in the company time zone.
// Every change saves with no reload: green "Saved ✓", or a red toast and the control reverts.

export const CHANNEL_LABELS = {
    email: 'Email',
    sms: 'SMS',
    push: 'Push',
    in_app: 'In-app',
    voice: 'Voice',
};
// Column order on the board: Email, SMS, Push, In-app, Voice.
export const CHANNEL_COLUMNS = ['email', 'sms', 'push', 'in_app', 'voice'].filter((channel) => NOTIFICATION_PREFERENCE_CHANNELS.includes(channel));

export const MOBILE_EXAMPLE_MESSAGE = 'Enter a valid mobile number, for example +1 (404) 555-1234.';
export const NO_STAFF_RECORD_MESSAGE = 'Your login isn\'t linked to a staff record yet. Ask an admin to add you in Staff & Roles.';
export const QUIET_HOURS_DEFAULT = { enabled: false, startsAt: '22:00', endsAt: '06:00' };

// Help lines written for this screen (no Self-Help article exists for these). Rows use the Self-Help text.
export const QUIET_HOURS_HELP = {
    what: 'A nightly window when your phone stays quiet.',
    who: 'Only you. It changes nothing for anyone else.',
    when: 'Every day between the two times, in the company time zone. No push or texts; critical alerts still come through; in-app and email still arrive.',
};
export const FALLBACK_ROW_HELP = {
    what: 'An alert IntelAgent sends to staff about your company.',
    who: 'Staff who handle this kind of work.',
    when: 'When the event happens, on the channels you leave ticked.',
};

function preferenceKey(eventType, channel) {
    return `${eventType}:${channel}`;
}

// This page is for the signed-in staff member: client and driver notifications go to clients and drivers.
export function appliesToStaff(workflow) {
    return String(workflow?.eventType || '').startsWith('staff.');
}

/** Personal settings can only narrow what the company sends: turning a channel ON needs the company to have it on. */
export function personalChannelChangeAllowed({ available, companyAllows }, enabled) {
    if (!available) return false;
    return enabled ? companyAllows === true : true;
}

function mobileSaveMessage(failure) {
    const message = failure?.message || '';
    if (/not linked|staff record|no staff|not a staff/i.test(message)) return NO_STAFF_RECORD_MESSAGE;
    return message || 'Your mobile number could not be saved.';
}

function hhmm(value, fallback) {
    const match = /^(\d{2}):(\d{2})/.exec(String(value || ''));
    return match ? `${match[1]}:${match[2]}` : fallback;
}

/** "Eastern Time (America/New_York)" for an IANA zone; the zone itself when the browser can't name it. */
export function timeZoneLabel(zone) {
    if (!zone) return '';
    try {
        const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longGeneric' }).formatToParts(new Date());
        const name = parts.find((part) => part.type === 'timeZoneName')?.value;
        return name && name !== zone ? `${name} (${zone})` : zone;
    } catch (_ignored) {
        return zone;
    }
}

// `embedded`: shown inside the Settings > Communications > Notifications modal, whose own title and subtitle replace this page header.
function PersonalNotificationPreferencesView({ userId, orgId, isAdmin = false, embedded = false }) {
    const toast = useSaveToast();
    const ids = useId();
    const [workflows, setWorkflows] = useState([]);
    const [allowedByEvent, setAllowedByEvent] = useState({});
    const [companyOff, setCompanyOff] = useState({});
    const [preferences, setPreferences] = useState({});
    const [companyName, setCompanyName] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const prefSeq = useRef({});

    const [mobile, setMobile] = useState('');
    const [savedMobile, setSavedMobile] = useState('');
    const [mobileError, setMobileError] = useState('');
    const [mobileSaving, setMobileSaving] = useState(false);
    const mobileInFlight = useRef(false);

    const [quiet, setQuiet] = useState(QUIET_HOURS_DEFAULT);
    const savedQuiet = useRef(QUIET_HOURS_DEFAULT);
    const quietSeq = useRef(0);
    const [quietError, setQuietError] = useState('');
    const [quietLoaded, setQuietLoaded] = useState(false);
    const [timeZone, setTimeZone] = useState('');

    useEffect(() => {
        let active = true;

        async function loadPreferences() {
            if (!userId || !orgId) {
                setError('Your organization and signed-in user could not be identified.');
                setLoading(false);
                return;
            }
            setLoading(true);
            setError('');
            const [workflowResult, userResult, switchResult] = await Promise.all([
                supabaseClient.rpc('bl395a_personal_notification_workflows'),
                supabaseClient
                    .from('notification_prefs')
                    .select('event_type, channel, enabled')
                    .eq('org_id', orgId)
                    .eq('user_id', userId),
                // The company-wide on/off switch per notification (no row = on). Optional: a failed read only
                // loses the "Off for <company>" row label; the channel list already reflects the switch.
                Promise.resolve(supabaseClient
                    .from('org_notification_switches')
                    .select('event_type, enabled')
                    .eq('org_id', orgId)).catch((failure) => ({ data: null, error: failure })),
            ]);
            if (!active) return;
            const failed = [workflowResult, userResult].find((result) => result.error);
            if (failed) {
                setError(failed.error.message || 'Notification preferences could not be loaded.');
                setLoading(false);
                return;
            }
            setWorkflows(normalizeNotificationWorkflows(workflowResult.data || []).filter(appliesToStaff));
            setAllowedByEvent((workflowResult.data || []).reduce((map, workflow) => ({
                ...map,
                [workflow.event_type]: workflow.available_channels || [],
            }), {}));
            setCompanyOff((switchResult?.error ? [] : switchResult?.data || []).reduce((map, row) => (
                row?.enabled === false ? { ...map, [row.event_type]: true } : map
            ), {}));
            setPreferences(buildOverrideMap(userResult.data || []));
            setLoading(false);
        }

        loadPreferences().catch((loadError) => {
            if (!active) return;
            setError(loadError.message || 'Notification preferences could not be loaded.');
            setLoading(false);
        });
        return () => { active = false; };
    }, [orgId, userId]);

    // Side lookups: none of these may block the page.
    useEffect(() => {
        let active = true;
        if (!userId || !orgId) return () => { active = false; };
        Promise.resolve(supabaseClient.rpc('bl177_get_my_staff_mobile')).then(({ data }) => {
            if (!active || typeof data !== 'string' || !data) return;
            setSavedMobile(data);
            setMobile(data);
        }).catch(() => {});
        // The company people know (Brands & Locations), not the tenant name ("VWH").
        loadCurrentCompanyName(supabaseClient, orgId).then((name) => {
            if (active && name) setCompanyName(name);
        }).catch(() => {});
        Promise.resolve(supabaseClient.rpc('org_time_zone', { p_org_id: orgId })).then(({ data }) => {
            if (active && typeof data === 'string' && data) setTimeZone(data);
        }).catch(() => {});
        Promise.resolve(supabaseClient
            .from('user_quiet_hours')
            .select('enabled, starts_at, ends_at')
            .eq('user_id', userId)
            .eq('org_id', orgId)
            .maybeSingle()).then(({ data, error: quietLoadError }) => {
            if (!active) return;
            if (quietLoadError) {
                setQuietError('Your quiet hours could not be loaded.');
                return;
            }
            const loaded = data ? {
                enabled: data.enabled === true,
                startsAt: hhmm(data.starts_at, QUIET_HOURS_DEFAULT.startsAt),
                endsAt: hhmm(data.ends_at, QUIET_HOURS_DEFAULT.endsAt),
            } : QUIET_HOURS_DEFAULT;
            savedQuiet.current = loaded;
            setQuiet(loaded);
            setQuietLoaded(true);
        }).catch(() => { if (active) setQuietError('Your quiet hours could not be loaded.'); });
        return () => { active = false; };
    }, [orgId, userId]);

    const company = companyName || 'your company';

    async function saveMobile() {
        if (mobileInFlight.current) return;
        const typed = String(mobile || '').trim();
        if (!typed && !savedMobile) return;
        const e164 = toE164(typed, 'US');
        if (!e164) {
            setMobileError(MOBILE_EXAMPLE_MESSAGE);
            return;
        }
        setMobileError('');
        if (e164 === savedMobile) return;
        mobileInFlight.current = true;
        setMobileSaving(true);
        try {
            const { data, error: rpcError } = await supabaseClient.rpc('bl177_set_my_staff_mobile', { p_mobile: e164 });
            if (rpcError) throw rpcError;
            const stored = typeof data === 'string' && data ? data : e164;
            setSavedMobile(stored);
            setMobile(stored);
            toast.saved();
        } catch (saveError) {
            const message = mobileSaveMessage(saveError);
            setMobileError(message);
            setMobile(savedMobile);
            toast.failed(message);
        } finally {
            mobileInFlight.current = false;
            setMobileSaving(false);
        }
    }

    function channelState(workflow, channel) {
        const key = preferenceKey(workflow.eventType, channel);
        const available = notificationPreferenceChannels(workflow).includes(channel);
        // available_channels comes from the send-path check itself (company switch, company channel override and the
        // critical-alert bypass all applied), so it is the one source of truth for what the company allows.
        const companyAllows = (allowedByEvent[workflow.eventType] || []).includes(channel);
        return { key, available, companyAllows, checked: available && companyAllows && (preferences[key] ?? true) };
    }

    async function setChannelPreference(workflow, channel, enabled) {
        const state = channelState(workflow, channel);
        const { key } = state;
        if (!personalChannelChangeAllowed(state, enabled)) {
            toast.failed(`That channel is turned off for ${company} by your company admin.`);
            return;
        }
        const previous = preferences[key];
        const seq = (prefSeq.current[key] || 0) + 1;
        prefSeq.current[key] = seq;
        setPreferences((current) => ({ ...current, [key]: enabled }));
        let failure = '';
        try {
            const { error: saveError } = await supabaseClient
                .from('notification_prefs')
                .upsert({
                    org_id: orgId,
                    user_id: userId,
                    event_type: workflow.eventType,
                    channel,
                    enabled,
                }, { onConflict: 'org_id,user_id,event_type,channel' });
            if (saveError) failure = saveError.message || 'Your preference could not be saved.';
        } catch (saveError) {
            failure = saveError?.message || 'Your preference could not be saved.';
        }
        if (prefSeq.current[key] !== seq) return;
        if (failure) {
            setPreferences((current) => {
                const next = { ...current };
                if (previous === undefined) delete next[key];
                else next[key] = previous;
                return next;
            });
            toast.failed(failure);
        } else {
            toast.saved();
        }
    }

    async function saveQuiet(next) {
        if (!quietLoaded) return;
        if (next.startsAt === next.endsAt) {
            setQuiet(savedQuiet.current);
            toast.failed('Quiet hours need different start and end times.');
            return;
        }
        const seq = ++quietSeq.current;
        setQuiet(next);
        let failure = '';
        try {
            const { error: saveError } = await supabaseClient
                .from('user_quiet_hours')
                .upsert({
                    user_id: userId,
                    org_id: orgId,
                    enabled: next.enabled,
                    starts_at: next.startsAt,
                    ends_at: next.endsAt,
                }, { onConflict: 'user_id,org_id' });
            if (saveError) failure = saveError.message || 'Your quiet hours could not be saved.';
        } catch (saveError) {
            failure = saveError?.message || 'Your quiet hours could not be saved.';
        }
        if (seq !== quietSeq.current) return;
        if (failure) {
            setQuiet(savedQuiet.current);
            toast.failed(failure);
        } else {
            savedQuiet.current = next;
            toast.saved();
        }
    }

    function commitQuietTime(field) {
        const value = quiet[field];
        if (!/^\d{2}:\d{2}$/.test(value || '')) {
            setQuiet(savedQuiet.current);
            return;
        }
        if (value !== savedQuiet.current[field]) saveQuiet({ ...savedQuiet.current, enabled: quiet.enabled, [field]: value });
    }

    if (loading) return <p className="ia-d2 p-4 text-sm">Loading your notification preferences…</p>;

    const zone = timeZoneLabel(timeZone);
    const quietDisabled = !quietLoaded;
    const inputClass = 'rounded-[10px] border border-[var(--ia-line)] bg-[#0A101C] px-3 py-2 font-semibold text-white shadow-[inset_0_2px_3px_rgba(0,0,0,.6)]';

    return (
        <section className={`ia-d2 mx-auto flex w-full max-w-5xl flex-col gap-5 rounded-2xl ${embedded ? 'p-0' : 'p-4 sm:p-6'}`} aria-labelledby={embedded ? undefined : `${ids}-title`} aria-label={embedded ? 'Notifications' : undefined}>
            {!embedded && (
                <header className="flex flex-col gap-1">
                    <h2 id={`${ids}-title`} className="text-2xl font-bold sm:text-3xl">My notifications</h2>
                    <p className="text-sm sm:text-base">How you hear about things. These only change what you get.</p>
                </header>
            )}

            <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))' }}>
                <WebPushDeviceControl client={supabaseClient} userId={userId} orgId={orgId} variant="card" />
                <section className="ia-d2-card flex flex-col gap-2" aria-label="Live alerts in this window" data-testid="live-alerts-card">
                    <h3 className="text-base font-bold">Alerts in this window</h3>
                    <LiveAlertsStatus />
                </section>
                <section className="ia-d2-card flex flex-col gap-3" aria-labelledby={`${ids}-mobile-label`}>
                    <form onSubmit={(event) => { event.preventDefault(); saveMobile(); }} noValidate className="flex flex-col gap-3">
                        <label id={`${ids}-mobile-label`} htmlFor="staff-alert-mobile" className="text-base font-bold">My mobile number</label>
                        <p id={`${ids}-mobile-help`} className="text-sm">For staff text alerts. Never shown to clients.</p>
                        {/* Saves when you press Enter or leave the box. */}
                        <div onBlur={() => saveMobile()}>
                            <PhoneInput
                                id="staff-alert-mobile"
                                value={mobile}
                                defaultCountry="US"
                                disabled={mobileSaving}
                                className={`w-full ${inputClass}`}
                                onChange={(next) => { setMobile(next); setMobileError(''); }}
                            />
                        </div>
                        {mobileError && <p role="alert" className="text-sm text-red-300">{mobileError}</p>}
                    </form>
                </section>
            </div>

            {error && <p role="alert" className="rounded-lg border border-red-500/60 bg-red-900/40 p-3 text-sm">{error}</p>}

            {workflows.length === 0 ? (
                <p className="text-sm">No staff alerts are set up for your company yet.</p>
            ) : (
                <>
                    <p data-testid="company-off-note" className="text-sm">
                        Grey items are turned off for {company} by your company admin.
                        {isAdmin && <> <AppLink to="/admin/notifications" className="underline">Change them in Notification Defaults</AppLink>.</>}
                    </p>
                    <div className="ia-d2-card ia-d2-scroll p-0">
                        <table className="w-full min-w-[600px] border-collapse text-left">
                            <thead>
                                <tr className="text-xs font-bold uppercase tracking-wider">
                                    <th scope="col" className="px-4 py-3">Alerts for staff
                                        <HelpButton title="Alerts for staff" userId={userId}>
                                            <span className="block">Choose your channels for each alert. Grey items are off for your company; personal choices cannot turn those on.</span>
                                            {workflows.map(workflow => {
                                                const help = NOTIFICATION_HELP_CONTENT[workflow.workflowKey];
                                                return <span key={workflow.workflowKey} className="mt-3 block"><strong>{workflow.displayName}</strong>
                                                    <span className="block">{help?.definition || FALLBACK_ROW_HELP.what}</span>
                                                    <span className="block"><strong>Who gets it:</strong> {help?.who || FALLBACK_ROW_HELP.who}</span>
                                                    <span className="block"><strong>When it’s sent:</strong> {help?.when || FALLBACK_ROW_HELP.when}</span>
                                                </span>;
                                            })}
                                        </HelpButton>
                                    </th>
                                    {CHANNEL_COLUMNS.map((channel) => (
                                        <th key={channel} scope="col" className="w-16 px-2 py-3 text-center">{CHANNEL_LABELS[channel]}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {workflows.map((workflow) => {
                                    const rowOff = Boolean(companyOff[workflow.eventType])
                                        && (allowedByEvent[workflow.eventType] || []).length === 0;
                                    return (
                                        <tr key={workflow.workflowKey} data-row={workflow.eventType} className={`border-t border-[var(--ia-line)] ${rowOff ? 'opacity-60' : ''}`}>
                                            <th scope="row" className="px-4 py-3 font-semibold">
                                                <span className="inline-flex flex-wrap items-center gap-2">
                                                    <span>{workflow.displayName}</span>
                                                    {rowOff && <span className="text-xs font-normal">Off for {company}</span>}
                                                </span>
                                            </th>
                                            {CHANNEL_COLUMNS.map((channel) => {
                                                const state = channelState(workflow, channel);
                                                return (
                                                    <td key={channel} className="px-2 py-3 text-center">
                                                        {state.available ? (
                                                            <Checkbox
                                                                aria-label={`${workflow.displayName}: ${CHANNEL_LABELS[channel]}`}
                                                                checked={state.checked}
                                                                disabled={!state.companyAllows}
                                                                onChange={(next) => setChannelPreference(workflow, channel, next)}
                                                            />
                                                        ) : (
                                                            <span aria-label={`${CHANNEL_LABELS[channel]} not available`} role="img">—</span>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            <section className="ia-d2-card flex flex-col gap-3" aria-labelledby={`${ids}-quiet-title`}>
                <h3 id={`${ids}-quiet-title`} className="inline-flex items-center gap-2 text-base font-bold">
                    Quiet hours
                    <HelpButton title="Quiet hours" what={QUIET_HOURS_HELP.what} who={QUIET_HOURS_HELP.who} when={QUIET_HOURS_HELP.when} />
                </h3>
                <div className="flex flex-wrap items-center gap-3 text-sm">
                    <Switch
                        label="Quiet hours"
                        checked={quiet.enabled}
                        disabled={quietDisabled}
                        describedBy={`${ids}-quiet-help`}
                        onChange={(next) => saveQuiet({ ...savedQuiet.current, enabled: next })}
                    />
                    <label htmlFor={`${ids}-quiet-from`}>No push or texts from</label>
                    <input
                        id={`${ids}-quiet-from`}
                        type="time"
                        value={quiet.startsAt}
                        disabled={quietDisabled}
                        className={`w-32 ${inputClass}`}
                        onChange={(event) => setQuiet((current) => ({ ...current, startsAt: event.target.value }))}
                        onBlur={() => commitQuietTime('startsAt')}
                    />
                    <label htmlFor={`${ids}-quiet-to`}>to</label>
                    <input
                        id={`${ids}-quiet-to`}
                        type="time"
                        value={quiet.endsAt}
                        disabled={quietDisabled}
                        className={`w-32 ${inputClass}`}
                        onChange={(event) => setQuiet((current) => ({ ...current, endsAt: event.target.value }))}
                        onBlur={() => commitQuietTime('endsAt')}
                    />
                    <span data-testid="quiet-zone">{zone ? `${zone}, the company time zone.` : 'Company time zone.'}</span>
                </div>
                <p id={`${ids}-quiet-help`} className="text-sm">
                    During quiet hours you get no push or texts. Critical alerts still come through. In-app and email still arrive.
                </p>
                {quietError && <p role="alert" className="text-sm text-red-300">{quietError}</p>}
            </section>

            <AlertSoundsCard client={supabaseClient} userId={userId} orgId={orgId} />
        </section>
    );
}

export default PersonalNotificationPreferencesView;
